import React, { useState, useEffect } from 'react';
import { LayoutDashboard, TrendingUp, Gamepad2, Trophy, Globe, Monitor, Users, Activity, Eye, EyeOff, Download } from 'lucide-react';
import { useAdmin } from '../contexts/AdminContext';
import { GA4Provider, useGA4 } from '../contexts/GA4Context';
import StatsOverview from './admin/StatsOverview';
import UserGrowthChart from './admin/UserGrowthChart';
import GameEngagementChart from './admin/GameEngagementChart';
import LeaderboardHealth from './admin/LeaderboardHealth';
import TrafficAnalyticsServer from './admin/TrafficAnalyticsServer';
import AcquisitionAnalytics from './admin/AcquisitionAnalytics';
import BehaviorAnalytics from './admin/BehaviorAnalytics';
import TechnologyAnalytics from './admin/TechnologyAnalytics';
import './AdminDashboard.css';

const ADMIN_PASSWORD = 'supersavage';
const AUTH_KEY = 'admin_authenticated';

const DashboardContent = () => {
  const { loading, analytics, refresh, allScores, dateRange, setDateRange } = useAdmin();
  const ga4Context = useGA4();
  const [activeTab, setActiveTab] = useState('overview');
  const [showExportMenu, setShowExportMenu] = useState(false);

  const exportGameRecordsCSV = (customRange = null) => {
    const rangeToUse = customRange || dateRange;

    // Parse date range
    const getDateRangeDates = (startDate, endDate) => {
      const now = new Date();
      let start, end;

      if (endDate === 'today') {
        end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
      } else if (endDate === 'yesterday') {
        const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        end = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate(), 23, 59, 59, 999);
      } else if (endDate === 'thisMonthStart') {
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        end = new Date(monthStart.getTime() - 1);
      } else {
        end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
      }

      if (startDate === 'today') {
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
      } else if (startDate === 'yesterday') {
        const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        start = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate(), 0, 0, 0, 0);
      } else if (startDate === '7daysAgo') {
        const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        start = new Date(sevenDaysAgo.getFullYear(), sevenDaysAgo.getMonth(), sevenDaysAgo.getDate(), 0, 0, 0, 0);
      } else if (startDate === '30daysAgo') {
        const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        start = new Date(thirtyDaysAgo.getFullYear(), thirtyDaysAgo.getMonth(), thirtyDaysAgo.getDate(), 0, 0, 0, 0);
      } else if (startDate === '90daysAgo') {
        const ninetyDaysAgo = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
        start = new Date(ninetyDaysAgo.getFullYear(), ninetyDaysAgo.getMonth(), ninetyDaysAgo.getDate(), 0, 0, 0, 0);
      } else if (startDate === 'thisMonthStart') {
        start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
      } else if (startDate === 'lastMonthStart') {
        start = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0, 0);
      } else if (startDate === 'thisYearStart') {
        start = new Date(now.getFullYear(), 0, 1, 0, 0, 0, 0);
      } else {
        start = new Date(startDate);
        start.setHours(0, 0, 0, 0);
      }

      return { start, end };
    };

    const { start: rangeStart, end: rangeEnd } = getDateRangeDates(rangeToUse.startDate, rangeToUse.endDate);

    // Filter scores by date range
    const filteredScores = allScores.filter(s => {
      const timestamp = s.timestamp?.toDate ? s.timestamp.toDate() : new Date(s.timestamp);
      return timestamp >= rangeStart && timestamp <= rangeEnd;
    });

    // CSV Headers
    const headers = ['Date', 'Time', 'Player Name', 'Mode', 'Difficulty', 'Completion Time (s)', 'Moves', 'Daily Challenge', 'Leaderboard Submission', 'Game ID'];

    // CSV Rows
    const rows = filteredScores.map(game => {
      const timestamp = game.timestamp?.toDate ? game.timestamp.toDate() : new Date(game.timestamp);
      return [
        timestamp.toLocaleDateString(),
        timestamp.toLocaleTimeString(),
        game.playerName || 'Anonymous',
        game.mode || '',
        game.difficulty || '',
        game.time || '',
        game.moves || '',
        game.isDailyChallenge ? 'Yes' : 'No',
        game.isLeaderboardSubmission ? 'Yes' : 'No',
        game.id || ''
      ];
    });

    // Create CSV content
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    // Download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `matcha-game-records-${rangeToUse.startDate}-to-${rangeToUse.endDate}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setShowExportMenu(false);
  };

  const exportGA4CSV = async () => {
    // Fetch current GA4 data from context
    const { metrics: ga4Metrics, timeSeries, acquisition, events, technology, pages, countries, sources } = ga4Context;

    const sections = [];

    // Section 1: Summary Metrics
    sections.push('=== SUMMARY METRICS ===');
    sections.push('Metric,Value');
    if (ga4Metrics) {
      sections.push(`Active Users,${ga4Metrics.activeUsers || 0}`);
      sections.push(`New Users,${ga4Metrics.newUsers || 0}`);
      sections.push(`Sessions,${ga4Metrics.sessions || 0}`);
      sections.push(`Avg Session Duration (s),${ga4Metrics.avgSessionDuration || 0}`);
      sections.push(`Bounce Rate,${ga4Metrics.bounceRate || 0}`);
      sections.push(`Engagement Rate,${ga4Metrics.engagementRate || 0}`);
    }
    sections.push('');

    // Section 2: Time Series
    if (timeSeries && timeSeries.length > 0) {
      sections.push('=== TIME SERIES DATA ===');
      sections.push('Date,Active Users,New Users,Sessions,Page Views,Avg Session Duration,Engagement Rate,Event Count');
      timeSeries.forEach(row => {
        sections.push(`${row.date},${row.activeUsers},${row.newUsers},${row.sessions},${row.pageViews},${row.avgSessionDuration},${row.engagementRate},${row.eventCount}`);
      });
      sections.push('');
    }

    // Section 3: Acquisition (Traffic Sources)
    if (acquisition && acquisition.length > 0) {
      sections.push('=== ACQUISITION / TRAFFIC SOURCES ===');
      sections.push('Channel,Source,Medium,Sessions,Users,New Users,Engagement Rate,Avg Session Duration');
      acquisition.forEach(row => {
        sections.push(`"${row.channel}","${row.source}","${row.medium}",${row.sessions},${row.users},${row.newUsers},${row.engagementRate},${row.avgSessionDuration}`);
      });
      sections.push('');
    }

    // Section 4: Events
    if (events && events.length > 0) {
      sections.push('=== EVENTS ===');
      sections.push('Event Name,Event Count');
      events.forEach(row => {
        sections.push(`"${row.eventName}",${row.eventCount}`);
      });
      sections.push('');
    }

    // Section 5: Technology
    if (technology && technology.length > 0) {
      sections.push('=== TECHNOLOGY (Device/Browser/OS) ===');
      sections.push('Device Category,Browser,Operating System,Users,Sessions,Avg Session Duration,Bounce Rate');
      technology.forEach(row => {
        sections.push(`"${row.deviceCategory}","${row.browser}","${row.os}",${row.users},${row.sessions},${row.avgSessionDuration},${row.bounceRate}`);
      });
      sections.push('');
    }

    // Section 6: Pages
    if (pages && pages.length > 0) {
      sections.push('=== PAGE PERFORMANCE ===');
      sections.push('Page Path,Page Title,Page Views,Avg Session Duration,Engagement Rate');
      pages.forEach(row => {
        sections.push(`"${row.pagePath}","${row.pageTitle}",${row.pageViews},${row.avgSessionDuration},${row.engagementRate}`);
      });
      sections.push('');
    }

    // Section 7: Countries
    if (countries && countries.length > 0) {
      sections.push('=== GEOGRAPHIC DATA ===');
      sections.push('Country,Users');
      countries.forEach(row => {
        sections.push(`"${row.country}",${row.users}`);
      });
      sections.push('');
    }

    // Section 8: Sources
    if (sources && sources.length > 0) {
      sections.push('=== TOP SOURCES ===');
      sections.push('Source,Users,Sessions');
      sources.forEach(row => {
        sections.push(`"${row.source}",${row.users},${row.sessions}`);
      });
    }

    const csvContent = sections.join('\n');

    // Download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `matcha-ga4-analytics-${dateRange.startDate}-to-${dateRange.endDate}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setShowExportMenu(false);
  };

  const exportPresets = [
    { label: 'Current Selection', value: null },
    { label: 'Today', startDate: 'today', endDate: 'today' },
    { label: 'Yesterday', startDate: 'yesterday', endDate: 'yesterday' },
    { label: 'Last 7 days', startDate: '7daysAgo', endDate: 'today' },
    { label: 'Last 30 days', startDate: '30daysAgo', endDate: 'today' },
    { label: 'This month', startDate: 'thisMonthStart', endDate: 'today' },
    { label: 'Last month', startDate: 'lastMonthStart', endDate: 'thisMonthStart' },
    { label: 'Last 90 days', startDate: '90daysAgo', endDate: 'today' },
    { label: 'This year', startDate: 'thisYearStart', endDate: 'today' },
    { label: 'All time', startDate: '2020-01-01', endDate: 'today' }
  ];

  if (loading) {
    return (
      <div className="admin-dashboard">
        <div className="admin-loading">
          <div className="spinner"></div>
          <p>Loading analytics...</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: LayoutDashboard },
    { id: 'growth', label: 'User Growth', icon: TrendingUp },
    { id: 'engagement', label: 'Game Engagement', icon: Gamepad2 },
    { id: 'leaderboard', label: 'Leaderboard', icon: Trophy },
    { id: 'traffic', label: 'Traffic Analytics', icon: Globe },
    { id: 'acquisition', label: 'Acquisition', icon: Users },
    { id: 'behavior', label: 'Behavior', icon: Activity },
    { id: 'technology', label: 'Technology', icon: Monitor }
  ];

  return (
    <div className="admin-dashboard">
        <div className="dashboard-main">
          <header className="admin-header">
            <div>
              <h1>Analytics Dashboard</h1>
              <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#6B7280' }}>
                Comprehensive analytics for Matcha
              </p>
            </div>
            <div className="header-actions">
              <div className="export-dropdown">
                <button
                  className="export-button"
                  onClick={() => setShowExportMenu(!showExportMenu)}
                >
                  <Download size={18} />
                  Export CSV
                </button>
                {showExportMenu && (
                  <>
                    <div
                      className="dropdown-backdrop"
                      onClick={() => setShowExportMenu(false)}
                      style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 98
                      }}
                    />
                    <div className="export-menu">
                      <div style={{ padding: '8px 16px', fontWeight: '600', fontSize: '12px', color: '#6B7280', borderBottom: '1px solid #E5E7EB' }}>
                        GAME RECORDS
                      </div>
                      {exportPresets.map((preset, index) => (
                        <button
                          key={index}
                          className="export-option"
                          onClick={() => preset.value === null ? exportGameRecordsCSV() : exportGameRecordsCSV(preset)}
                        >
                          {preset.label}
                        </button>
                      ))}
                      <div style={{ padding: '8px 16px', fontWeight: '600', fontSize: '12px', color: '#6B7280', borderTop: '2px solid #E5E7EB', borderBottom: '1px solid #E5E7EB', marginTop: '4px' }}>
                        GA4 ANALYTICS
                      </div>
                      <button
                        className="export-option"
                        onClick={exportGA4CSV}
                      >
                        Export All GA4 Data
                      </button>
                    </div>
                  </>
                )}
              </div>
              <button className="refresh-button" onClick={refresh}>
                Refresh
              </button>
            </div>
          </header>

          <nav className="admin-tabs">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  className={`nav-item ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <Icon size={20} />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>

          <div className="admin-content">
            {activeTab === 'overview' && (
              <>
                <StatsOverview analytics={analytics} />
                <div className="dashboard-grid">
                  <UserGrowthChart analytics={analytics} />
                  <GameEngagementChart analytics={analytics} />
                </div>
              </>
            )}

            {activeTab === 'growth' && (
              <div className="dashboard-section">
                <UserGrowthChart analytics={analytics} fullSize />
              </div>
            )}

            {activeTab === 'engagement' && (
              <div className="dashboard-section">
                <GameEngagementChart analytics={analytics} fullSize />
              </div>
            )}

            {activeTab === 'leaderboard' && (
              <div className="dashboard-section">
                <LeaderboardHealth analytics={analytics} />
              </div>
            )}

            {activeTab === 'traffic' && (
              <div className="dashboard-section">
                <TrafficAnalyticsServer />
              </div>
            )}

            {activeTab === 'acquisition' && (
              <div className="dashboard-section">
                <AcquisitionAnalytics />
              </div>
            )}

            {activeTab === 'behavior' && (
              <div className="dashboard-section">
                <BehaviorAnalytics />
              </div>
            )}

            {activeTab === 'technology' && (
              <div className="dashboard-section">
                <TechnologyAnalytics />
              </div>
            )}
          </div>
        </div>
      </div>
  );
};

const AdminDashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [showError, setShowError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    // Check if already authenticated in sessionStorage
    const auth = sessionStorage.getItem(AUTH_KEY);
    if (auth === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (passwordInput === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      sessionStorage.setItem(AUTH_KEY, 'true');
      setShowError(false);
    } else {
      setShowError(true);
      setPasswordInput('');
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="admin-dashboard">
        <div className="admin-login">
          <div className="login-card">
            <h1>Matcha Admin</h1>
            <form onSubmit={handlePasswordSubmit}>
              <div className="password-field">
                <input
                  type={showPassword ? "text" : "password"}
                  value={passwordInput}
                  onChange={(e) => setPasswordInput(e.target.value)}
                  placeholder="Enter password"
                  className={`password-input ${showError ? 'error' : ''}`}
                  autoFocus
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {showError && <p className="error-message">Incorrect password</p>}
              <button type="submit" className="login-button">
                Login
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <GA4Provider>
      <DashboardContent />
    </GA4Provider>
  );
};

export default AdminDashboard;
